"use client";

import { useState } from "react";
import { ScrollArea } from "@/components/ui/scroll-area";

export default function PrivacyPolicyPage() {
  const [selectedSection, setSelectedSection] = useState("Definitions");

  const sections = [
    "Definitions",
    "Key Terms",
    "Collection of Personal Information",
    "Types of Personal Information Collected",
    "How We Use Personal Information",
    "Authentication & Registration",
    "Limited Use Policy",
    "Data Security & Protection",
    "Use of Cookies",
    "Access to Personal Information",
    "Sharing of Personal Information",
    "International Data Transfers",
    "Data Retention",
    "Third-Party Links",
    "Your Rights",
    "Updates to This Policy",
    "Contact Us",
  ];

  const sectionContent = {
    Definitions: (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Definitions</h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>Site: Our website.</li>
          <li>Services: All products and services we offer.</li>
          <li>
            You/Your: The individual or entity accessing our Site or using our
            Services.
          </li>
        </ul>
      </>
    ),
    "Key Terms": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Key Terms</h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>
            Data Controller: The entity that determines how and why personal
            information is processed.
          </li>
          <li>
            Data Processor: The entity that processes personal information on
            behalf of the Data Controller.
          </li>
          <li>
            Data Processing: Any operation performed on personal information.
          </li>
          <li>Personal Information: Data that identifies an individual.</li>
        </ul>
      </>
    ),
    "Collection of Personal Information": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Collection of Personal Information
        </h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>Directly from you when you provide it to us.</li>
          <li>From third parties that share information about you.</li>
        </ul>
      </>
    ),
    "Types of Personal Information Collected": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Types of Personal Information Collected
        </h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>
            Account Information: User ID, username, and email associated with
            UPO AI Hub.
          </li>
          <li>Team Information: Team name, identifier, and related details.</li>
        </ul>
      </>
    ),
    "How We Use Personal Information": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          How We Use Personal Information
        </h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>Provide, maintain, and enhance our services.</li>
          <li>Analyze usage patterns and statistics.</li>
          <li>Personalize your experience.</li>
          <li>Improve customer service and support.</li>
          <li>
            Send periodic updates, including service notifications and
            promotional emails.
          </li>
          <li>
            Process transactions for service purchases, renewals, and licensing.
          </li>
          <li>Comply with legal obligations.</li>
        </ul>
      </>
    ),
    "Authentication & Registration": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Authentication & Registration
        </h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>Google OAuth (provided by Google)</li>
          <li>Discord OAuth (provided by Discord Inc.)</li>
        </ul>
      </>
    ),
    "Limited Use Policy": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Limited Use Policy
        </h2>

        <p className="mt-4 text-gray-700">
          UPO AI Hub adheres to the Google API Services User Data Policy and
          Discord’s Developer Terms of Service.
        </p>
      </>
    ),
    "Data Security & Protection": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Data Security & Protection
        </h2>

        <p className="mt-4 text-gray-700">
          We take data security seriously and implement technical,
          organizational, and administrative measures to safeguard your personal
          information.
        </p>
      </>
    ),
    "Use of Cookies": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Use of Cookies</h2>

        <p className="mt-4 text-gray-700">
          We use cookies for session management and secure authentication.
        </p>
      </>
    ),
    "Access to Personal Information": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Access to Personal Information
        </h2>

        <p className="mt-4 text-gray-700">
          Authorized personnel at UPO AI Hub may access personal data only when
          necessary for customer support, security investigations, or
          compliance. All such activities are logged for accountability.
        </p>
      </>
    ),
    "Sharing of Personal Information": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Sharing of Personal Information
        </h2>

        <p className="mt-4 text-gray-700">
          We do not sell or trade personal data. However, we may share
          information:
        </p>
        <ul className="mt-4 text-gray-700 space-y-2">
          <li>With affiliates under common ownership with UPO AI Hub.</li>
          <li>
            To comply with legal obligations, enforce policies, or protect
            rights.
          </li>
          <li>In case of business mergers, acquisitions, or restructuring.</li>
        </ul>
      </>
    ),
    "International Data Transfers": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          International Data Transfers
        </h2>

        <p className="mt-4 text-gray-700">
          Personal data may be stored and processed in various countries,
          including the United States. We ensure adequate security measures when
          transferring data across borders.
        </p>
      </>
    ),
    "Data Retention": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Data Retention</h2>

        <p className="mt-4 text-gray-700">
          We retain personal data for as long as necessary to provide services
          and comply with legal obligations. Some data may be archived for
          regulatory or operational purposes.
        </p>
      </>
    ),
    "Third-Party Links": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Third-Party Links
        </h2>

        <p className="mt-4 text-gray-700">
          Our services may include links to external websites. We are not
          responsible for their privacy policies or content.
        </p>
      </>
    ),
    "Your Rights": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Your Rights</h2>

        <ul className="mt-4 text-gray-700 space-y-2">
          <li>Request a copy of your personal data.</li>
          <li>Correct inaccurate or incomplete information.</li>
          <li>Transfer your data to another entity.</li>
          <li>Request data deletion.</li>
        </ul>
      </>
    ),
    "Updates to This Policy": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">
          Updates to This Policy
        </h2>

        <p className="mt-4 text-gray-700">
          We may update this Privacy Policy from time to time.
        </p>
      </>
    ),
    "Contact Us": (
      <>
        <h2 className="text-2xl font-semibold text-gray-900">Contact Us</h2>

        <p className="mt-4 text-gray-700">
          For inquiries, email up@uponly.com.
        </p>
      </>
    ),
  };

  const scrollToSection = (sectionKey: string) => {
    setSelectedSection(sectionKey);
    const sectionElement = document.getElementById(sectionKey);
    if (sectionElement) {
      sectionElement.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <div className="w-full relative pt-[72px]">
      {/* Header Section */}
      <div className="w-full flex flex-col items-center bg-gradient-to-b from-[#F3E9E1] to-[#EADFD6]">
        <div className="text-center pt-[67px] pb-[91px] max-w-3xl">
          <div className="inline-block px-5 py-2 rounded-full bg-white text-[#14151A] shadow-md border border-gray-300 text-center font-wix text-captionL leading-captionL tracking-captionL font-medium">
            Privacy Policy
          </div>

          <h1 className="text-4xl font-semibold text-[#9378E8] mt-6">
            Your privacy is a priority for us.
          </h1>
          <p className="text-[#0F1324] opacity-60 text-lg mt-4 leading-relaxed">
            This Privacy Policy outlines how we collect, use, and disclose
            information obtained through our services. It does not extend to
            third-party websites, services, or applications, even if they are
            accessible through our platform.
          </p>
        </div>
      </div>

      {/* Main Content Section */}
      <div className="w-full max-w-6xl mx-auto flex bg-white overflow-hidden mt-10">
        {/* Sidebar Navigation */}
        <aside className="w-56 px-5 py-6">
          <ScrollArea className="h-[85vh]">
            <ul>
              {sections.map((section) => (
                <li
                  key={section}
                  className={`cursor-pointer px-3 py-2 text-sm font-medium transition-all ${
                    selectedSection === section
                      ? "text-[#14151A] font-semibold border-l-4 border-[#7C3AED] bg-white"
                      : "text-[#0F132499] hover:text-[#9378E8] border-l-2 border-[#E5E5E5]"
                  }`}
                  onClick={() => scrollToSection(section)}
                >
                  {section}
                </li>
              ))}
            </ul>
          </ScrollArea>
        </aside>

        {/* Content Panel */}
        <div className="flex-1 px-16 py-12 bg-white space-y-12">
          {Object.entries(sectionContent).map(([sectionKey, content]) => (
            <div key={sectionKey} id={sectionKey} className="scroll-mt-20">
              {content}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
